import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiInstance from '../../api/api';
import apiEndpoints from '../../utils/api/endpoints';

interface KozelBattleState {
  loading: boolean;
  error: string | null;
  uuid: string | null;
  userLightScore: number;
  userDarkScore: number;
  totalLightScore: number;
  totalDarkScore: number;
}

const initialState: KozelBattleState = {
  loading: false,
  error: null,
  uuid: localStorage.getItem('kozelBattleUuid'),
  userLightScore: 0,
  userDarkScore: 0,
  totalLightScore: 0,
  totalDarkScore: 0,
};

export const generateKozelUserId = createAsyncThunk(
  'kozelBattle/generateUserId',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.post(apiEndpoints.kozelBattleGenerateId);
      if (response.data?.success && response.data?.unique_id) {
        return response.data.unique_id as string;
      }
      return rejectWithValue('Failed to generate user ID');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Could not generate user ID'
      );
    }
  }
);

export const addKozelScore = createAsyncThunk(
  'kozelBattle/addScore',
  async (
    params: { unique_id: string; team: 'light' | 'dark'; score: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiInstance.post(apiEndpoints.kozelBattleAddScore, params);
      if (response.data?.success) {
        return response.data as { light_score: number; dark_score: number };
      }
      return rejectWithValue(response.data?.message || 'Failed to add score');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Could not save score'
      );
    }
  }
);

export const fetchKozelTotals = createAsyncThunk(
  'kozelBattle/fetchTotals',
  async () => {
    try {
      const response = await apiInstance.get(apiEndpoints.kozelBattleTotals);
      if (response.data?.success) {
        return {
          lightTotal: Number(response.data.light_total) || 0,
          darkTotal: Number(response.data.dark_total) || 0,
        };
      }
      return { lightTotal: 0, darkTotal: 0 };
    } catch {
      return { lightTotal: 0, darkTotal: 0 };
    }
  }
);

export const fetchKozelUserScores = createAsyncThunk(
  'kozelBattle/fetchUserScores',
  async (unique_id: string, { rejectWithValue }) => {
    try {
      const response = await apiInstance.get(
        `${apiEndpoints.kozelBattleUserScores}/${unique_id}`
      );
      if (response.data?.success) {
        return {
          lightScore: Number(response.data.light_score) || 0,
          darkScore: Number(response.data.dark_score) || 0,
        };
      }
      return rejectWithValue('Player not found');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Could not fetch user scores'
      );
    }
  }
);

const kozelBattleSlice = createSlice({
  name: 'kozelBattle',
  initialState,
  reducers: {
    clearKozelError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateKozelUserId.fulfilled, (state, action) => {
        state.uuid = action.payload;
        localStorage.setItem('kozelBattleUuid', action.payload);
      })
      .addCase(generateKozelUserId.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(addKozelScore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addKozelScore.fulfilled, (state, action) => {
        state.loading = false;
        state.userLightScore = action.payload.light_score;
        state.userDarkScore = action.payload.dark_score;
      })
      .addCase(addKozelScore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchKozelTotals.fulfilled, (state, action) => {
        state.totalLightScore = action.payload.lightTotal;
        state.totalDarkScore = action.payload.darkTotal;
      })
      .addCase(fetchKozelUserScores.fulfilled, (state, action) => {
        state.userLightScore = action.payload.lightScore;
        state.userDarkScore = action.payload.darkScore;
      });
  },
});

export const { clearKozelError } = kozelBattleSlice.actions;
export default kozelBattleSlice.reducer;